import CompactDoc from './CompactDoc';
import { meetingInsightsSectionAriaLabel } from './Insights.locstring.json';
import getInsightDocRowIcon from './getInsightDocRowIcon';
import type {
    EmailInsightType,
    FileInsightType,
    InsightsDataFetchSource,
} from 'owa-meeting-insights-types-and-flights';
import { observer } from 'mobx-react-lite';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import * as React from 'react';
import loc from 'owa-localize';
import { AriaRoles } from 'owa-accessibility';

import styles from './InsightDocRow.scss';
export const MAX_CONTENT_TO_SHOW_INITIALLY = 3;

export interface InsightDocListProps {
    files: FileInsightType[];
    emails?: EmailInsightType[];
    meetingId: string;
    colorScheme: ColorScheme;
    iconClass?: string;
    compactDocClass?: string;
    entrySource: InsightsDataFetchSource;
    getShowMoreButton: () => React.FunctionComponent<{
        onClick: () => void;
        shouldShowMore: boolean;
        colorScheme: ColorScheme;
    }>;
}

export default observer(function InsightDocList(props: InsightDocListProps) {
    const {
        files,
        emails,
        colorScheme,
        iconClass,
        getShowMoreButton,
        meetingId,
        compactDocClass,
        entrySource,
    } = props;

    const [allInsightsAreShown, setAllInsightsAreShown] = React.useState<boolean>(false);

    const allInsights = [...files, ...(emails || [])];
    if (allInsights.length === 0) {
        return null;
    }

    const shouldShowButton = allInsights.length > MAX_CONTENT_TO_SHOW_INITIALLY;
    const numToDisplay = allInsightsAreShown
        ? allInsights.length
        : Math.min(allInsights.length, MAX_CONTENT_TO_SHOW_INITIALLY);

    const ShowMoreButton = getShowMoreButton();
    const toggleShowMoreInsights = () => {
        // Toggle allInsightsAreShown as the button can be show more/show less.
        setAllInsightsAreShown(!allInsightsAreShown);
    };

    return (
        <div tabIndex={0} className={styles.insightsSection}>
            {getInsightDocRowIcon(files, colorScheme, iconClass)}
            <div className={styles.secondColumn}>
                {renderInsightsList(
                    meetingId,
                    allInsights,
                    numToDisplay,
                    compactDocClass,
                    entrySource
                )}
                {shouldShowButton && (
                    <ShowMoreButton
                        onClick={toggleShowMoreInsights}
                        shouldShowMore={!allInsightsAreShown /* shouldShowMore */}
                        colorScheme={colorScheme}
                    />
                )}
            </div>
        </div>
    );
});

function renderInsightsList(
    meetingId: string,
    allInsights: (FileInsightType | EmailInsightType)[],
    numToDisplay: number,
    compactDocClass: string,
    entrySource: InsightsDataFetchSource
): JSX.Element {
    const insightsList = [];
    for (let i = 0; i < numToDisplay; i++) {
        const insight = allInsights[i];
        insightsList.push(
            <CompactDoc
                type={insight.type as 'Document' | 'Message'}
                key={insight.referenceId}
                insight={insight}
                meetingId={meetingId}
                compactDocClass={compactDocClass}
                entrySource={entrySource}
                ariaRole={AriaRoles.listitem}
            />
        );
    }
    return (
        <div
            className={styles.fileList}
            role={AriaRoles.list}
            aria-label={loc(meetingInsightsSectionAriaLabel)}>
            {insightsList}
        </div>
    );
}
